'use client';

import type { SocialMedia } from '@baseball-near-you/common';
import type { Address } from '@baseball-near-you/prisma';
import {
  AspectRatio,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@baseball-near-you/ui';
import { SocialIconMapping } from '@bb/lib/SocialMedia';
import { useSpecificTeam } from '@bb/lib/hooks/useSpecificTeam';
import { useSpecificTeamGrades } from '@bb/lib/hooks/useSpecificTeamGrades';
import { getInitials } from '@bb/lib/utils/getInitials';
import { HelpCircle, MapPinIcon, Verified } from 'lucide-react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { type ReactNode, useState } from 'react';
import { BulletinBoard } from './BulletinBoard';
import { ReadMore } from './ReadMore';

import { TeamGradeStars } from './Reviews/Grading/TeamGradeStar';
import { TeamSelectionModal } from './Settings/Team/ClaimTeam/TeamSelectionModal';

export function TeamHeader() {
  const params = useParams();
  const { team } = useSpecificTeam(params.slug as string);
  const { averageGrades, overallGrade } = useSpecificTeamGrades();

  const [imageError, setImageError] = useState(false);

  return (
    <div className="z-0 mx-auto h-full w-full rounded-lg border bg-white">
      <div className="relative inset-0 overflow-hidden rounded-t-md">
        <div className="relative max-h-48 w-full rounded-b-md">
          {team?.bannerPhotoUrl ? (
            <AspectRatioImage
              imageUrl={team?.bannerPhotoUrl ?? undefined}
              setImageError={setImageError}
              imageSizing={() => 'w-full rounded-b-none border-0'}
              priority
              sizes="(max-width: 768px) 80vw, (max-width: 1200px) 60vw, 40vw"
            />
          ) : (
            <div className="h-24 w-full bg-gray-300! md:h-48" />
          )}
        </div>
      </div>
      <div className="md:relative">
        <div className="-translate-y-1/2 absolute z-10 flex w-full transform">
          <div className="-translate-y-1/2 absolute top-1/2 left-5 z-10 w-40 transform">
            <AspectRatioImage
              imageUrl={team?.logoUrl ?? undefined}
              setImageError={setImageError}
              imageSizing={(ratio) => {
                return cn(
                  ratio === 0 && 'w-24 min-w-[6rem]',
                  (ratio || 0) > 1 ? 'w-36 min-w-[9rem]' : 'w-20 min-w-[5rem]'
                );
              }}
              imageFallback={
                team?.name ? <p>{getInitials(team.name)}</p> : null
              }
              priority
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 20vw, 15vw"
            />
          </div>
          <div className="z-10 mr-5 ml-auto hidden space-x-3 md:flex">
            {/* <a href="#team-breakdown">
                <Button
                  className={cn(
                    'bg-blue-700 hover:bg-blue-800',
                    !!team?.bannerPhotoUrl && !imageError && 'border-black'
                  )}
                >
                  Team details
                </Button>
              </a> */}
            {!!team && team.approvedClaims.length === 0 && (
              <div className="flex items-center">
                <TeamSelectionModal
                  selectedTeamSlug={params.slug as string}
                  triggerNode={({ open, setOpen }) => (
                    <Button
                      onClick={() => setOpen(!open)}
                      className={cn(
                        'title sm:!py-1.5 w-full rounded-lg bg-red px-4.5 font-bold text-base text-white sm:w-auto md:ml-6'
                      )}
                      type="button"
                    >
                      Claim team
                    </Button>
                  )}
                />
                {/* <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="hidden sm:inline">
                      <HelpCircle className="ml-2 size-6" />
                    </TooltipTrigger>
                    <TooltipContent className="mb-1 w-52" side="right">
                      <p className="text-gray-700 text-sm">
                        You may claim this profile if you are a coach or owner
                        of this organization.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> */}
              </div>
            )}
            <a href="#reviews-section">
              <Button
                className={cn(
                  'bg-slate-600 px-4.5 text-base hover:bg-slate-600/[95%] ',
                  !!team?.bannerPhotoUrl && !imageError && 'border-black'
                )}
              >
                Reviews
              </Button>
            </a>
          </div>
        </div>

        <div
          className={cn(
            'w-full space-y-5 rounded-b-md px-5 pb-5.5',
            team?.logoUrl && !imageError ? 'pt-16' : 'pt-12',
            !team?.logoUrl && 'pt-12',
            team && team?.updates.length > 0 && 'rounded-b-none shadow-xs'
          )}
        >
          <div className="flex w-full flex-col justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div className="flex flex-col">
              <div className="flex flex-col sm:flex">
                <div className="flex items-center gap-x-2">
                  <h1 className="title !mb-0 font-semibold text-3xl text-gray-900">
                    {team?.name.toUpperCase()}
                  </h1>
                  {!!team && team.approvedClaims.length > 0 && (
                    <Verified className="fill-red stroke-white" size={30} />
                  )}
                </div>
                {!!team?.address[0] && (
                  <TeamLocation address={team.address[0]} className="mt-1!" />
                )}
                <div className="flex flex-col items-start space-y-3 sm:mt-5 sm:flex-row sm:items-center sm:justify-start sm:space-y-0">
                  <div className="mt-2 flex items-center space-x-2 sm:mt-0">
                    {Object.entries(team?.socials ?? {}).map(([name, link]) => {
                      const Icon = SocialIconMapping[name as SocialMedia];
                      return link ? (
                        <a
                          href={link}
                          key={name}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <button
                            className="group flex size-9 items-center justify-center rounded-full border-2 border-red bg-red transition-all hover:scale-[101%] hover:bg-white"
                            type="button"
                          >
                            {/* TODO figure out better fix for PG icon, rn if icon is null its PG  */}
                            {Icon ? (
                              <Icon
                                size={19}
                                className="stroke-white group-hover:stroke-red"
                              />
                            ) : (
                              <span className="title pt-0.5 pl-[0.05rem] text-white group-hover:text-red">
                                PG
                              </span>
                            )}
                          </button>
                        </a>
                      ) : null;
                    })}
                  </div>

                  {team && team.approvedClaims.length === 0 && (
                    <div className="flex flex-col border-t pt-4 sm:hidden">
                      <div className="flex items-center">
                        <TeamSelectionModal
                          selectedTeamSlug={params.slug as string}
                          triggerNode={({ open, setOpen }) => (
                            <Button
                              onClick={() => setOpen(!open)}
                              className={cn(
                                'title sm:!py-1.5 w-full rounded-lg bg-red px-3 font-bold text-base text-white sm:w-auto md:ml-6'
                              )}
                              type="button"
                            >
                              Claim team
                            </Button>
                          )}
                        />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="hidden sm:inline">
                              <HelpCircle className="ml-2 size-6" />
                            </TooltipTrigger>
                            <TooltipContent className="mb-1 w-52" side="right">
                              <p className="text-gray-700 text-sm">
                                You may claim this profile if you are a coach or
                                owner of this organization.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="mt-2 font-normal text-gray-800 text-sm sm:hidden">
                        You may claim this profile if you are a coach or owner
                        of this organization.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!!overallGrade && (
              <div className="hidden h-min w-min flex-col items-start justify-between self-end rounded-md bg-gray-100 px-4 py-3 sm:flex">
                <p className="title mb-1 text-black text-lg">overall rating</p>
                <TeamGradeStars grade={overallGrade} />
              </div>
            )}
          </div>
          {!!team?.description && (
            <ReadMore charCount={300} text={team.description} />
          )}
        </div>
      </div>
      {team && team?.updates.length > 0 && (
        <BulletinBoard bulletins={team.updates} />
      )}
    </div>
  );
}

export function TeamLocation({
  address,
  className,
  pinIconSize,
}: {
  address: Address;
  className?: string;
  pinIconSize?: number;
}) {
  const location = address;
  return location.city && location.state ? (
    <div className={cn('mt-2 flex items-center', className)}>
      <MapPinIcon size={pinIconSize} className={cn('mr-1 text-red/75')} />
      <h3 className="title text-gray-500">
        {location.city}, {location.state}
      </h3>
    </div>
  ) : (
    <></>
  );
}

export function AspectRatioImage({
  imageUrl,
  setImageError,
  imageSizing,
  imageFallback,
  priority,
  sizes,
}: {
  imageUrl?: string | null;
  setImageError?: (error: boolean) => void;
  imageSizing: (ratio: number, imageLoaded: boolean) => string;
  imageFallback?: ReactNode;
  priority?: boolean;
  sizes?: string;
}) {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    const ratio = naturalWidth / naturalHeight;
    setAspectRatio(ratio);
    setImageLoaded(true);
  };

  return (
    <div
      className={cn(
        'w-24 overflow-hidden rounded-sm border border-gray-600 bg-white shadow-xs transition-opacity',
        !aspectRatio ? 'opacity-0' : 'opacity-100',
        imageSizing(aspectRatio ?? 0, imageLoaded)
      )}
    >
      <AspectRatio ratio={aspectRatio ?? 1}>
        <div className="relative h-full w-full">
          {imageUrl ? (
            <Image
              fill
              alt="team logo"
              src={imageUrl}
              onLoad={handleImageLoad}
              onError={() => setImageError?.(true)}
              priority={!!priority}
              sizes={sizes}
              fetchPriority={priority ? 'high' : 'auto'}
            />
          ) : (
            imageFallback
          )}
        </div>
      </AspectRatio>
    </div>
  );
}
